import { random } from 'lodash-es';
import queryString from 'query-string';
import type { Category, DressItem } from './types';

// CDN 기본 경로
export const CDN_BASE_PATH = `${process.env.NEXT_PUBLIC_CDN_URL}/public/games`;

// 프리로드용 이미지 URL 생성 함수 (f=avif가 w보다 앞에 오도록)
const createPreloadImageUrl = (src: string, w: number): string => {
  return queryString.stringifyUrl(
    {
      url: src,
      query: { f: 'avif', w }, // f=avif가 w보다 앞에 오도록 순서 보장
    },
    {
      skipNull: true,
      skipEmptyString: true,
      sort: false, // 정렬하지 않음 (삽입 순서 유지)
    },
  );
};

// UI 이미지 상수 (src와 w 분리)
export const UI_IMAGES = {
  BG: { src: `${CDN_BASE_PATH}/ch_bg.png`, w: 1760 },
  SLOT_BG: { src: `${CDN_BASE_PATH}/ch_slot_bg.png`, w: 1760 },
  SLOT_FRAME: { src: `${CDN_BASE_PATH}/ch_slot_frame.png`, w: 1760 },
  ARROW: { src: `${CDN_BASE_PATH}/ch_arrow.png`, w: 200 },
  INTRO: { src: `${CDN_BASE_PATH}/ch_intro.png`, w: 1760 },
  OUTRO: { src: `${CDN_BASE_PATH}/ch_outro.png`, w: 1760 },
  SPACEBAR: { src: `${CDN_BASE_PATH}/ch_spacebar.png`, w: 308 },
  FRAME_1: { src: `${CDN_BASE_PATH}/ch_frame_1_final.png`, w: 400 },
  FRAME_2: { src: `${CDN_BASE_PATH}/ch_frame_2_final.png`, w: 400 },
} as const;

// BGM 오디오 상수
export const BGM_AUDIO = {
  BGM_1: `${CDN_BASE_PATH}/ch_bgm_1.mp3`,
} as const;

// 이미지 경로 상수 배열 (CharacterPreview 154px, SlotMachine 124px 중 큰 값 * 2 = 308)
const FACE_IMAGES = [
  { src: `${CDN_BASE_PATH}/ch_face_1.png`, w: 308 },
  { src: `${CDN_BASE_PATH}/ch_face_2.png`, w: 308 },
  { src: `${CDN_BASE_PATH}/ch_face_3.png`, w: 308 },
  { src: `${CDN_BASE_PATH}/ch_face_4.png`, w: 308 },
  { src: `${CDN_BASE_PATH}/ch_face_5.png`, w: 308 },
];

const COSTUME_IMAGES = [
  { src: `${CDN_BASE_PATH}/ch_costume_1.png`, w: 308 },
  { src: `${CDN_BASE_PATH}/ch_costume_2.png`, w: 308 },
  { src: `${CDN_BASE_PATH}/ch_costume_3.png`, w: 308 },
  { src: `${CDN_BASE_PATH}/ch_costume_4.png`, w: 308 },
  { src: `${CDN_BASE_PATH}/ch_costume_5.png`, w: 308 },
];

const CLOTH_IMAGES = [
  { src: `${CDN_BASE_PATH}/ch_cloth_0.png`, w: 308 },
  { src: `${CDN_BASE_PATH}/ch_cloth_1.png`, w: 308 },
  { src: `${CDN_BASE_PATH}/ch_cloth_2.png`, w: 308 },
  { src: `${CDN_BASE_PATH}/ch_cloth_3.png`, w: 308 },
  { src: `${CDN_BASE_PATH}/ch_cloth_4.png`, w: 308 },
  { src: `${CDN_BASE_PATH}/ch_cloth_5.png`, w: 308 },
];

const BACKGROUND_IMAGES = [
  { src: `${CDN_BASE_PATH}/ch_bg_1.png`, w: 308 },
  { src: `${CDN_BASE_PATH}/ch_bg_2.png`, w: 308 },
  { src: `${CDN_BASE_PATH}/ch_bg_3.png`, w: 308 },
  { src: `${CDN_BASE_PATH}/ch_bg_4.png`, w: 308 },
  { src: `${CDN_BASE_PATH}/ch_bg_5.png`, w: 308 },
];

const EFFECT_IMAGES = [
  { src: `${CDN_BASE_PATH}/ch_ef_1.png`, w: 308 },
  { src: `${CDN_BASE_PATH}/ch_ef_2.png`, w: 308 },
  { src: `${CDN_BASE_PATH}/ch_ef_3.png`, w: 308 },
  { src: `${CDN_BASE_PATH}/ch_ef_4.png`, w: 308 },
  { src: `${CDN_BASE_PATH}/ch_ef_5.png`, w: 308 },
];

// 카테고리별 아이템 데이터
export const DRESS_ITEMS: Record<Category, DressItem[]> = {
  expression: FACE_IMAGES.map((image, index) => ({
    id: `exp-${index}`,
    category: 'expression',
    name: `표정 ${index + 1}`,
    emoji: image.src,
    imageWidth: image.w,
    layer: 2,
  })),
  hat: COSTUME_IMAGES.map((image, index) => ({
    id: `hat-${index + 1}`,
    category: 'hat',
    name: `모자 ${index + 1}`,
    emoji: image.src,
    imageWidth: image.w,
    layer: 4,
  })),
  top: CLOTH_IMAGES.map((image, index) => ({
    id: `top-${index}`,
    category: 'top',
    name: `상의 ${index + 1}`,
    emoji: image.src,
    imageWidth: image.w,
    layer: 1,
  })),
  background: BACKGROUND_IMAGES.map((image, index) => ({
    id: `bg-${index + 1}`,
    category: 'background',
    name: `배경 ${index + 1}`,
    emoji: image.src,
    imageWidth: image.w,
    layer: 0,
  })),
  effect: EFFECT_IMAGES.map((image, index) => ({
    id: `eff-${index + 1}`,
    category: 'effect',
    name: `이펙트 ${index + 1}`,
    emoji: image.src,
    imageWidth: image.w,
    layer: 5,
  })),
};

// 랜덤으로 N개의 아이템을 선택하는 함수
export const getRandomItems = (category: Category, count = 5): DressItem[] => {
  const items = DRESS_ITEMS[category];
  // Fisher-Yates shuffle 알고리즘 사용 (균등한 분포 보장)
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = random(0, i, false);
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, Math.min(count, items.length));
};

// 프리로드할 모든 이미지 URL 목록 (f=avif가 w보다 앞에 오도록)
export const ALL_PRELOAD_IMAGES = [
  // UI 이미지들
  ...Object.values(UI_IMAGES).map((img) => createPreloadImageUrl(img.src, img.w)),
  // 아이템 이미지들
  ...FACE_IMAGES.map((img) => createPreloadImageUrl(img.src, img.w)),
  ...COSTUME_IMAGES.map((img) => createPreloadImageUrl(img.src, img.w)),
  ...CLOTH_IMAGES.map((img) => createPreloadImageUrl(img.src, img.w)),
  ...BACKGROUND_IMAGES.map((img) => createPreloadImageUrl(img.src, img.w)),
  ...EFFECT_IMAGES.map((img) => createPreloadImageUrl(img.src, img.w)),
];

// 이미지 프리로드 함수
export const preloadImages = (): Promise<void[]> => {
  return Promise.all(
    ALL_PRELOAD_IMAGES.map(
      (src) =>
        new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve(); // 에러가 나도 계속 진행
          img.src = src;
        }),
    ),
  );
};
